import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { GIT_PROVIDERS } from '../../consts/git';
import { useWizardAnalytics } from '../../hooks/useWizardAnalytics';
import { Azure } from '../../logos';
import { QUERY_KEYS } from '../../network/queries/consts';
import { ROUTES } from '../../routes/consts';
import {
  DialogTitleWithIcon,
  MultiStepDialog,
} from '../../shared/MultiStepDialog';
import { spawn } from '../../utils/translations';
import { AzureInstallationProvider } from './azureInstallationContext';
import { getSteps } from './configuration';
import { CONNECT_REPOS_STEP_INDEX, OAUTH_PARAMS } from './consts';
import { clearConsentState } from './utils';

const getDefaultFurthestStepIndex = (
  pathname: string,
  search: string,
): number => {
  const isOAuthCallback = pathname === ROUTES.AZURE_INSTALLATION_STEP_3;
  const params = new URLSearchParams(search);
  const hasOAuthParams =
    params.has('error') ||
    params.has('error_description') ||
    params.has('success') ||
    params.has('code');

  return isOAuthCallback && hasOAuthParams ? CONNECT_REPOS_STEP_INDEX : 0;
};

export const AzureInstallationDialog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const spawnGeneral = spawn('dialogs.azure-installation.general');

  const defaultFurthestStepIndex = getDefaultFurthestStepIndex(
    location.pathname,
    location.search,
  );

  const steps = getSteps();
  const {
    onStepViewed,
    onStepNavigated,
    onCancel: trackClose,
    onWizardCompleted,
  } = useWizardAnalytics({
    gitProvider: GIT_PROVIDERS.AZURE,
    wizardVersion: 'v1',
    steps,
  });

  useEffect(() => {
    return () => {
      clearConsentState();
    };
  }, []);

  const close = () => {
    clearConsentState();

    const cleaned = new URLSearchParams(searchParams);

    OAUTH_PARAMS.forEach((param) => cleaned.delete(param));
    const cleanedSearch = cleaned.toString();

    navigate(
      {
        pathname: ROUTES.INTEGRATIONS,
        search: cleanedSearch ? `?${cleanedSearch}` : '',
      },
      { replace: true },
    );
  };

  const onCancel = (stepIndex: number, stepId: string) => {
    trackClose(stepIndex, stepId);
    close();
  };

  const onComplete = () => {
    onWizardCompleted();
    close();
    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.INSTALLATIONS_COUNTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GIT_INSTALLATIONS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GIT_REPOSITORIES],
      });
    }, 0);
  };

  return (
    <AzureInstallationProvider>
      <MultiStepDialog
        title={
          <DialogTitleWithIcon text={spawnGeneral('title')}>
            <Azure size={24} />
          </DialogTitleWithIcon>
        }
        steps={steps}
        onCancel={onCancel}
        onComplete={onComplete}
        onStepViewed={onStepViewed}
        onStepNavigated={onStepNavigated}
        defaultFurthestStepIndex={defaultFurthestStepIndex}
        className="sm:max-w-237!"
        contentClassName="text-body"
        disableOutsideClick={true}
        disableEscapeKey={true}
        outletClassName="w-[500px] flex flex-col"
      />
    </AzureInstallationProvider>
  );
};
